import React, { useState, useEffect, useContext } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Badge,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/css/donordashboard/DonationHistory.css";
import MasterLayout from "../../components/layout/MasterLayout";
import AppContext from "../../context/AppContext";
import axios from "axios";

function DonationHistory() {
  const { baseUrl } = useContext(AppContext);

  const [dhSearchTerm, setDhSearchTerm]     = useState("");
  const [dhFilterStatus, setDhFilterStatus] = useState("all");
  const [dhFilterType, setDhFilterType]     = useState("all");
  const [allDonations, setAllDonations]     = useState([]);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    fetchAllDonations();
  }, []);

  // ========================= FETCH ALL APIs =========================
  const fetchAllDonations = async () => {
    setLoading(true);
    try {
      const [bloodRes, foodRes, medicineRes, receiverReqRes] = await Promise.allSettled([
        axios.get(`${baseUrl}/donor/dashboard/viewAllBloodDonations`), // Blood
        axios.get(`${baseUrl}/donor/dashboard/viewAllDonation`),       // Food
        axios.get(`${baseUrl}/donor/dashboard/viewAllMedicine`),       // Medicine
        axios.get(`${baseUrl}/receiver/blood/viewAllRequests`),        // Receiver Requests
      ]);

      let combined = [];

      // ---------- BLOOD (donor ki apni) ----------
      if (bloodRes.status === "fulfilled" && bloodRes.value.data.success) {
        const list = bloodRes.value.data.donation || [];
        const mapped = list.map((d) => ({
          id:           d.id,
          date:         d.blood_date || d.created_at || "",
          campaignName: `Blood Donation — ${d.blood_group || ""}`,
          type:         "Blood",
          typeIcon:     "🩸",
          status:       d.status || "pending",
          detail:       `${d.donation_type || "Whole Blood"} | ${d.blood_units ? d.blood_units + " Unit(s)" : ""} | ${d.city || ""}`,
          receiptId:    `BLD-${d.id}`,
          source:       "donor",
        }));
        combined = [...combined, ...mapped];
      }

      // ---------- RECEIVER BLOOD REQUESTS (jo donor ne confirm ki) ----------
      if (receiverReqRes.status === "fulfilled" && receiverReqRes.value.data.success) {
        const list = receiverReqRes.value.data.requests || [];
        // Sirf approved wali dikhao (jo donor ne confirm ki hain)
        const mapped = list
          .filter((r) => (r.status || "").toLowerCase() === "approved")
          .map((r) => ({
            id:           `req-${r.id}`,
            date:         r.updated_at || r.created_at || "",
            campaignName: `Blood Donated to — ${r.patient_name || ""}`,
            type:         "Blood",
            typeIcon:     "🩸",
            status:       "approved",
            detail:       `${r.blood_group || ""} | ${r.units ? r.units + " Unit(s)" : ""} | ${r.hospital || ""} | ${r.city || ""}`,
            receiptId:    `RBD-${r.id}`,
            source:       "receiver",
          }));
        combined = [...combined, ...mapped];
      }

      // ---------- FOOD ----------
      if (foodRes.status === "fulfilled" && foodRes.value.data.success) {
        const list = foodRes.value.data.donation || [];
        const mapped = list.map((d) => ({
          id:           d.id,
          date:         d.pickup_date || d.created_at || "",
          campaignName: `Food Donation — ${d.food_item_name || ""}`,
          type:         "Food",
          typeIcon:     "🍱",
          status:       d.status || "pending",
          detail:       `${d.food_category || ""} | ${d.approx_people_served || ""} people | ${d.city || ""}`,
          receiptId:    `FD-${d.id}`,
          source:       "donor",
        }));
        combined = [...combined, ...mapped];
      }

      // ---------- MEDICINE ----------
      if (medicineRes.status === "fulfilled" && medicineRes.value.data.success) {
        const list = medicineRes.value.data.donations || [];
        const mapped = list.map((d) => ({
          id:           d.id,
          date:         d.pickup_date || d.created_at || "",
          campaignName: `Medicine Donation — ${d.medicine_name || ""}`,
          type:         "Medicine",
          typeIcon:     "💊",
          status:       d.donation || d.status || "pending",
          detail:       `${(d.medicine_category || "").replace("_", " ")} | ${d.quantity || ""} ${d.quantity_unit || ""} | ${d.city || ""}`,
          receiptId:    `MED-${d.id}`,
          source:       "donor",
        }));
        combined = [...combined, ...mapped];
      }

      // Sort latest first
      combined.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAllDonations(combined);
    } catch (error) {
      console.log("Error fetching donations:", error);
    } finally {
      setLoading(false);
    }
  };

  // ========================= SUMMARY =========================
  const dhTotalDonations = allDonations.length;
  const dhBloodCount     = allDonations.filter((d) => d.type === "Blood").length;
  const dhFoodCount      = allDonations.filter((d) => d.type === "Food").length;
  const dhMedicineCount  = allDonations.filter((d) => d.type === "Medicine").length;

  // ========================= FILTER =========================
  const dhFilteredDonations = allDonations.filter((donation) => {
    const matchesSearch =
      donation.campaignName.toLowerCase().includes(dhSearchTerm.toLowerCase());
    const matchesStatus =
      dhFilterStatus === "all" ||
      donation.status.toLowerCase() === dhFilterStatus.toLowerCase();
    const matchesType =
      dhFilterType === "all" ||
      donation.type.toLowerCase() === dhFilterType.toLowerCase();
    return matchesSearch && matchesStatus && matchesType;
  });

  // ========================= STATUS BADGE =========================
  const dhGetStatusBadge = (status) => {
    const map = {
      completed: { bg: "success", label: "Completed" },
      pending:   { bg: "warning", label: "Pending"   },
      verified:  { bg: "info",    label: "Verified"  },
      collected: { bg: "primary", label: "Collected" },
      picked_up: { bg: "info",    label: "Picked Up" },
      delivered: { bg: "success", label: "Delivered" },
      cancelled: { bg: "danger",  label: "Cancelled" },
      failed:    { bg: "danger",  label: "Failed"    },
      approved:  { bg: "success", label: "Approved ✓"},
    };
    const key = (status || "pending").toLowerCase();
    const s = map[key] || { bg: "secondary", label: status || "Pending" };
    return <Badge bg={s.bg}>{s.label}</Badge>;
  };

  // ========================= TYPE BADGE =========================
  const dhGetTypeBadge = (type, icon, source) => {
    const colorMap = {
      Blood:    "danger",
      Food:     "success",
      Medicine: "primary",
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
        <Badge bg={colorMap[type] || "secondary"}>
          {icon} {type}
        </Badge>
        {source === "receiver" && (
          <Badge bg="dark" style={{ fontSize: "0.65rem" }}>
            👤 For Receiver
          </Badge>
        )}
      </div>
    );
  };

  // ========================= FORMAT DATE =========================
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day:   "numeric",
      year:  "numeric",
    });
  };

  // ========================= RECEIPT =========================
  const dhHandleDownloadReceipt = (receiptId) => {
    alert(`Downloading receipt: ${receiptId}`);
  };

  // ========================= RETURN =========================
  return (
    <div className="dh-donation-history-container">
      <MasterLayout>

        {/* Page Header */}
        <div className="dh-page-header">
          <h1 className="dh-page-title">Donation History</h1>
          <p className="dh-page-subtitle">
            Track all your Blood, Food & Medicine donations
          </p>
        </div>

        {/* ===== SUMMARY CARDS ===== */}
        <Row className="dh-summary-section mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="dh-summary-card">
              <Card.Body>
                <div className="dh-summary-icon dh-total-donations">🎯</div>
                <div className="dh-summary-content">
                  <h3 className="dh-summary-value">{dhTotalDonations}</h3>
                  <p className="dh-summary-label">Total Donations</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="dh-summary-card">
              <Card.Body>
                <div className="dh-summary-icon" style={{ fontSize: "2rem" }}>🩸</div>
                <div className="dh-summary-content">
                  <h3 className="dh-summary-value">{dhBloodCount}</h3>
                  <p className="dh-summary-label">Blood Donations</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="dh-summary-card">
              <Card.Body>
                <div className="dh-summary-icon" style={{ fontSize: "2rem" }}>🍱</div>
                <div className="dh-summary-content">
                  <h3 className="dh-summary-value">{dhFoodCount}</h3>
                  <p className="dh-summary-label">Food Donations</p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} md={6} className="mb-3">
            <Card className="dh-summary-card">
              <Card.Body>
                <div className="dh-summary-icon" style={{ fontSize: "2rem" }}>💊</div>
                <div className="dh-summary-content">
                  <h3 className="dh-summary-value">{dhMedicineCount}</h3>
                  <p className="dh-summary-label">Medicine Donations</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ===== FILTERS ===== */}
        <Card className="dh-filter-card mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={5} md={12} className="mb-3 mb-lg-0">
                <InputGroup>
                  <InputGroup.Text>🔍</InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by donation name..."
                    value={dhSearchTerm}
                    onChange={(e) => setDhSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>

              <Col lg={3} md={6} className="mb-3 mb-lg-0">
                <Form.Select
                  value={dhFilterType}
                  onChange={(e) => setDhFilterType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="blood">🩸 Blood</option>
                  <option value="food">🍱 Food</option>
                  <option value="medicine">💊 Medicine</option>
                </Form.Select>
              </Col>

              <Col lg={4} md={6}>
                <Form.Select
                  value={dhFilterStatus}
                  onChange={(e) => setDhFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="approved">Approved</option>
                  <option value="completed">Completed</option>
                  <option value="collected">Collected</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ===== TABLE ===== */}
        <Card className="dh-table-card">
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading your donations...</p>
              </div>
            ) : (
              <div className="dh-table-responsive">
                <Table hover striped bordered className="dh-donations-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Donation</th>
                      <th>Type</th>
                      <th>Details</th>
                      <th>Status</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dhFilteredDonations.length > 0 ? (
                      dhFilteredDonations.map((donation, index) => (
                        <tr
                          key={`${donation.type}-${donation.id}`}
                          style={
                            donation.source === "receiver"
                              ? { background: "#fff8f8" }
                              : {}
                          }
                        >
                          <td>{index + 1}</td>
                          <td className="dh-date-column">
                            {formatDate(donation.date)}
                          </td>
                          <td className="dh-campaign-column">
                            <strong>{donation.campaignName}</strong>
                          </td>
                          <td>
                            {dhGetTypeBadge(donation.type, donation.typeIcon, donation.source)}
                          </td>
                          <td className="text-muted small">
                            {donation.detail}
                          </td>
                          <td className="dh-status-column">
                            {dhGetStatusBadge(donation.status)}
                          </td>
                          <td className="dh-receipt-column">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => dhHandleDownloadReceipt(donation.receiptId)}
                            >
                              📥 Download
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="dh-no-data">
                            <h4>No donations found</h4>
                            <p>Try adjusting your filters</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Count */}
        {!loading && dhFilteredDonations.length > 0 && (
          <div className="text-center mt-3 mb-4 text-muted">
            Showing <strong>{dhFilteredDonations.length}</strong> of{" "}
            <strong>{allDonations.length}</strong> donations
          </div>
        )}

      </MasterLayout>
    </div>
  );
}

export default DonationHistory;